
import { useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Upload, Check } from "lucide-react";

export interface ReceiptUploadProps {
  expenseId: string | number;
  organizationId: string;
  onUploadComplete: (fileUrl: string, fileName: string) => void;
  reportStatus?: string;
  children?: ReactNode;
  isUuid?: boolean;
  compact?: boolean; // Added compact prop for mobile view
}

const ReceiptUpload = ({ 
  expenseId, 
  organizationId, 
  onUploadComplete, 
  reportStatus = "pending", 
  children, 
  isUuid = false,
  compact = false // Default to false for backward compatibility
}: ReceiptUploadProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [file, setFile] = useState<File | null>(null);

  // Allow uploading for draft, rejected, and pending (default) statuses
  const isEditable = reportStatus !== "approved";

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };

  const uploadReceipt = async () => {
    if (!file) return;

    try {
      setIsUploading(true);
      console.log("Uploading receipt for expense ID:", expenseId, "Type:", typeof expenseId);

      // First delete any existing receipts for this expense
      // Check if expenseId is a UUID or a number
      const expenseIdStr = String(expenseId);
      const isExpenseUuid = isUuid || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(expenseIdStr);
      
      if (isExpenseUuid) {
        // Handle as UUID (likely from expense report items)
        const { data: existingReceipts, error: fetchError } = await supabase
          .from("receipts")
          .select("*")
          .eq("expense_report_item_id", expenseIdStr);

        if (fetchError) {
          console.error("Error fetching existing receipts:", fetchError);
        } else if (existingReceipts && existingReceipts.length > 0) {
          // Delete files from storage
          for (const receipt of existingReceipts) {
            const fileName = receipt.file_url.split('/').pop();
            if (fileName) {
              const { error: deleteFileError } = await supabase.storage
                .from("receipts")
                .remove([fileName]);

              if (deleteFileError) {
                console.error("Error deleting file:", deleteFileError);
              }
            }
          }

          // Delete receipt records
          const { error: deleteError } = await supabase
            .from("receipts")
            .delete()
            .eq("expense_report_item_id", expenseIdStr);

          if (deleteError) {
            console.error("Error deleting receipt records:", deleteError);
          }
        }
      } else {
        // Handle as numeric expense ID
        let numericId: number;
        
        if (typeof expenseId === 'string') {
          numericId = parseInt(expenseId, 10);
          if (isNaN(numericId)) {
            throw new Error("Invalid expense ID: Not a valid number");
          }
        } else {
          numericId = expenseId as number;
        }
        
        const { data: existingReceipts, error: fetchError } = await supabase
          .from("receipts")
          .select("*")
          .eq("expense_id", numericId);

        if (fetchError) {
          console.error("Error fetching existing receipts:", fetchError);
        } else if (existingReceipts && existingReceipts.length > 0) {
          // Delete files from storage
          for (const receipt of existingReceipts) {
            const fileName = receipt.file_url.split('/').pop();
            if (fileName) {
              const { error: deleteFileError } = await supabase.storage
                .from("receipts")
                .remove([fileName]);

              if (deleteFileError) {
                console.error("Error deleting file:", deleteFileError);
              }
            }
          }

          // Delete receipt records
          const { error: deleteError } = await supabase
            .from("receipts")
            .delete()
            .eq("expense_id", numericId);

          if (deleteError) {
            console.error("Error deleting receipt records:", deleteError);
          }
        }
      }

      // Upload the new file
      const fileExt = file.name.split('.').pop();
      const fileName = `receipt_${Date.now()}.${fileExt}`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("receipts")
        .upload(fileName, file);

      if (uploadError) {
        throw new Error(`Error uploading file: ${uploadError.message}`);
      }

      if (!uploadData) {
        throw new Error("Upload failed - no data returned");
      }

      // Get public URL for the file
      const { data: publicUrlData } = supabase.storage
        .from("receipts")
        .getPublicUrl(fileName);

      const publicUrl = publicUrlData.publicUrl;

      // Store receipt information in the database with organization_id
      let insertData = {
        file_url: publicUrl,
        file_name: file.name,
        uploaded_by: (await supabase.auth.getUser()).data.user?.id,
        organization_id: organizationId
      };
      
      // Add the expense_id with the correct type
      if (isExpenseUuid) {
        // For expense report items (UUID)
        const { error: insertError } = await supabase
          .from("receipts")
          .insert({
            ...insertData,
            expense_report_item_id: expenseIdStr,
            expense_id: null
          });

        if (insertError) {
          throw new Error(`Error recording receipt: ${insertError.message}`);
        }
      } else {
        // For regular expenses (numeric IDs)
        let numericId: number;
        
        if (typeof expenseId === 'string') {
          numericId = parseInt(expenseId, 10);
          if (isNaN(numericId)) {
            throw new Error("Invalid expense ID: Not a valid number");
          }
        } else {
          numericId = expenseId as number;
        }
        
        const { error: insertError } = await supabase
          .from("receipts")
          .insert({
            ...insertData,
            expense_id: numericId,
            expense_report_item_id: null
          });

        if (insertError) {
          throw new Error(`Error recording receipt: ${insertError.message}`);
        }
      }

      // Call the callback with the public URL
      onUploadComplete(publicUrl, file.name);
      setFile(null);
    } catch (error) {
      console.error("Error in upload process:", error);
      alert(`Upload failed: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <input
        type="file"
        id={`receipt-upload-${expenseId}`}
        accept="image/*,.pdf"
        className="hidden"
        onChange={handleFileChange}
        disabled={!isEditable || isUploading}
      />
      {!file ? (
        children ? (
          <div onClick={() => document.getElementById(`receipt-upload-${expenseId}`)?.click()}>
            {children}
          </div>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={() => document.getElementById(`receipt-upload-${expenseId}`)?.click()}
            disabled={!isEditable || isUploading}
            className={compact ? "p-2 h-8 w-8" : ""}
          >
            <Upload className="h-4 w-4" />
            {!compact && <span className="ml-1">Upload Receipt</span>}
          </Button>
        )
      ) : (
        <div className="flex items-center gap-2">
          <span className="text-sm truncate max-w-[100px]">{file.name}</span>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={uploadReceipt}
            disabled={isUploading}
            className={compact ? "p-2 h-8 w-8" : ""}
          >
            {isUploading ? (
              <div className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {!compact && "Uploading..."}
              </div>
            ) : (
              <>
                <Check className="h-4 w-4" />
                {!compact && <span className="ml-1">Confirm</span>}
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ReceiptUpload;
